import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { trekkingTours, destinations, weekendTrips } from '../../data/tourData';
import './SearchModal.css';

const SearchModal = ({ isOpen, onClose, initialQuery = '' }) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  // Combine all searchable data
  const allData = [
    ...trekkingTours.map(item => ({ ...item, type: 'trekking', category: 'Trekking Tours' })),
    ...destinations.map(item => ({ ...item, type: 'destination', category: 'Destinations' })),
    ...weekendTrips.map(item => ({ ...item, type: 'weekend', category: 'Weekend Trips' }))
  ];

  useEffect(() => {
    if (searchQuery.trim()) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        performSearch(searchQuery);
        setIsSearching(false);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  useEffect(() => {
    if (initialQuery) {
      setSearchQuery(initialQuery);
    }
  }, [initialQuery]);

  const performSearch = (query) => {
    const filteredResults = allData.filter(item => {
      const searchTerm = query.toLowerCase();
      return (
        item.name.toLowerCase().includes(searchTerm) ||
        item.region?.toLowerCase().includes(searchTerm) ||
        item.highlights?.some(highlight => highlight.toLowerCase().includes(searchTerm)) ||
        item.description?.toLowerCase().includes(searchTerm)
      );
    });

    // Sort by relevance
    const sortedResults = filteredResults.sort((a, b) => {
      const aNameMatch = a.name.toLowerCase().includes(query.toLowerCase());
      const bNameMatch = b.name.toLowerCase().includes(query.toLowerCase());
      
      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;
      return 0;
    });

    setSearchResults(sortedResults);
  };

  const getFilteredResults = () => {
    if (activeTab === 'all') return searchResults;
    return searchResults.filter(item => item.type === activeTab);
  };

  const getTabCounts = () => {
    const counts = { all: searchResults.length };
    ['trekking', 'destination', 'weekend'].forEach(type => {
      counts[type] = searchResults.filter(item => item.type === type).length;
    });
    return counts;
  };

  const tabCounts = getTabCounts();

  const handleResultClick = () => {
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="search-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="search-modal"
            initial={{ opacity: 0, scale: 0.9, y: -50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -50 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="search-modal-header">
              <div className="search-input-container">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search destinations, tours, or experiences..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                />
                <button className="close-btn" onClick={onClose}>
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="search-tabs">
              {[
                { key: 'all', label: 'All Results' },
                { key: 'trekking', label: 'Trekking' },
                { key: 'destination', label: 'Destinations' },
                { key: 'weekend', label: 'Weekend Trips' }
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                  {tabCounts[tab.key] > 0 && (
                    <span className="tab-count">{tabCounts[tab.key]}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Results */}
            <div className="search-results">
              {isSearching ? (
                <div className="search-loading">
                  <div className="loading-spinner"></div>
                  <p>Searching...</p>
                </div>
              ) : searchQuery.trim() ? (
                getFilteredResults().length > 0 ? (
                  <div className="results-grid">
                    {getFilteredResults().map((item, index) => (
                      <motion.div
                        key={`${item.type}-${item.id}`}
                        className="search-result-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={handleResultClick}
                      >
                        <Link to={item.link} className="result-link">
                          <div className="result-image">
                            <img src={item.image} alt={item.name} />
                            <div className="result-category">{item.category}</div>
                          </div>
                          <div className="result-content">
                            <h3>{item.name}</h3>
                            <div className="result-details">
                              {item.region && (
                                <span className="detail-item">
                                  <FaMapMarkerAlt />
                                  {item.region}
                                </span>
                              )}
                              {item.duration && (
                                <span className="detail-item">
                                  <FaCalendarAlt />
                                  {item.duration}
                                </span>
                              )}
                              {item.difficulty && (
                                <span className="detail-item">
                                  <FaStar />
                                  {item.difficulty}
                                </span>
                              )}
                            </div>
                            {item.price && (
                              <div className="result-price">{item.price}</div>
                            )}
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    <FaSearch className="no-results-icon" />
                    <h3>No results found</h3>
                    <p>Try searching with different keywords</p>
                  </div>
                )
              ) : (
                <div className="search-suggestions">
                  <h3>Popular Searches</h3>
                  <div className="suggestion-tags">
                    {['Himalayas', 'Kashmir', 'Ladakh', 'Spiti Valley', 'Trekking', 'Weekend Trips'].map(tag => (
                      <button
                        key={tag}
                        className="suggestion-tag"
                        onClick={() => setSearchQuery(tag)}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;
